// DEPENDENCIES
//// Third-Party
import { DateTime } from "luxon";
//// Local
import { IAlert, IAlertComment } from "../../../models/alert.model";
import { IIdentityDetail } from "../../../models/identity-detail.model";
import { IAlertAction } from "../../../models/alert-action.model";
import { IAlerts, IAlertState, IAlertWindow, ISelectedAlerts } from "../alert.states";
import { alertMatchesCurrentFilter, sortAlertsByFilter } from "./filter.helpers";
import { buildUniqueWindow, DEFAULT_EMPTY_WINDOW, findAlertGroupIndex, getInitialWindow, validateWindow } from "./window.helpers";
import { validateSelectedAlert, validateSelectedAlerts } from "./selection.helpers";
import { PAGE_SIZE } from "../../../constants/filter";
import { IAlertFilter } from "../../../../modules/alerts/filter";
import { IAlertLock } from "../../../../modules/alerts/alert-group";


const INVALID_FILTER_ALERTS_CULL_MODIFIER = 10;


function _ensureWindowAlerts(originalState: IAlertState): IAlertState {
    const fullAlertIDs: string[] = originalState.alerts.items.map(alert => alert.id);
    const fullAlertsToAdd: IAlert[] = originalState.window.fullAlerts.filter(alert => !fullAlertIDs.includes(alert.id));
    const newAlertItems: IAlert[] = [...originalState.alerts.items, ...fullAlertsToAdd];
    return {
        ...originalState,
        alerts: {
            ...originalState.alerts,
            items: newAlertItems
        }
    }
}

function _getUpdatedAlerts(originalState: IAlertState, newAlerts: IAlert[]): IAlert[] {
    const alertMap = new Map<string, IAlert>();
    for (const alert of originalState.alerts.items) {
        alertMap.set(alert.id, alert)
    };
    for (const alert of originalState.window.fullAlerts) {
        alertMap.set(alert.id, alert)
    }
    for (const newAlert of newAlerts) {
        alertMap.set(newAlert.id, newAlert)
    };

    const mergedAlerts: IAlert[] = Array.from(alertMap.values());
    return sortAlertsByFilter(originalState.filter.ordering, mergedAlerts);
}

// Below one page the store holds the entire filtered result set, so the local item
// count is the ground truth and drift is impossible by construction. Above one page
// the reported (server) figure is authoritative, clamped so the count never reads
// below what is already loaded locally.
export function resolveAlertTotal(reportedTotal: number, alertItems: IAlert[], filter: IAlertFilter): number {
    const matchingItemCount: number = alertItems.filter(alert => alertMatchesCurrentFilter(alert, filter)).length;
    if (reportedTotal <= PAGE_SIZE) {
        return matchingItemCount;
    }
    return Math.max(reportedTotal, matchingItemCount);
}

function _getUpdatedTotal(originalState: IAlertState, updatedAlertItems: IAlert[]): number {
    const originalAlertsTotal: number = originalState.alerts.items.filter(e => alertMatchesCurrentFilter(e, originalState.filter)).length;
    const newAlertsTotal: number = updatedAlertItems.filter(e => alertMatchesCurrentFilter(e, originalState.filter)).length;
    const alertTotalDifference: number = newAlertsTotal - originalAlertsTotal;
    const provisionalTotal: number = Math.max(0, originalState.alerts.total + alertTotalDifference);
    return resolveAlertTotal(provisionalTotal, updatedAlertItems, originalState.filter);
}

export function getMaxPage(total: number): number {
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
}

function _getUpdatedPagination(currentPagination: number, updatedAlertsTotal: number): number {
    const lastPossiblePaginationNumber: number = getMaxPage(updatedAlertsTotal);
    return currentPagination > lastPossiblePaginationNumber ? lastPossiblePaginationNumber : currentPagination
}

function _getStateAfterAlertUpdates(originalState: IAlertState, newAlertItems: IAlert[]): IAlertState {
    const updatedAlerts: IAlert[] = _getUpdatedAlerts(originalState, newAlertItems);
    const updatedAlertsTotal: number = _getUpdatedTotal(originalState, updatedAlerts);
    return {
        ...originalState,
        alerts: {
            ...originalState.alerts,
            items: updatedAlerts.filter((alert) => alertMatchesCurrentFilter(alert, originalState.filter)),
            total: updatedAlertsTotal
        },
        window: {
            ...originalState.window,
            fullAlerts: updatedAlerts.filter(e => originalState.window.alerts.includes(e.id))
        },
        page: {
            ...originalState.page,
            current: _getUpdatedPagination(originalState.page.current, updatedAlertsTotal)
        }
    }
}

export function getValidAlerts(state: IAlertState, alertsOverride: IAlert[] | undefined = undefined): IAlert[] {
    const unfilteredAlerts: IAlert[] = alertsOverride || state.alerts.items
    const alerts: IAlert[] = unfilteredAlerts.filter(e => alertMatchesCurrentFilter(e, state.filter))
    return sortAlertsByFilter(state.filter.ordering, alerts)
}

// Socket && API Based Updates
export function handleAlertAdd(originalState: IAlertState, alertsToAddInput: IAlert[]): Partial<IAlertState> {
    const processedAlertsInfo: { id: string; alertDataAfterProcessing: IAlert }[] = [];

    const newItemsMap = new Map(alertsToAddInput.map(item => [item.id, item]));
    const existingItemsMap = new Map(originalState.alerts.items.map(item => [item.id, item]));
    alertsToAddInput.forEach(alertToAdd => {
        processedAlertsInfo.push({ id: alertToAdd.id, alertDataAfterProcessing: alertToAdd });
    });
    const newAlertItems: IAlert[] = Array.from(newItemsMap.values()).filter(alert => !existingItemsMap.has(alert.id));

    const stateAfterAlertUpdate: IAlertState = _getStateAfterAlertUpdates(originalState, newAlertItems);

    const finalValidatedSelectedAlerts: ISelectedAlerts = validateSelectedAlerts(
        stateAfterAlertUpdate.alerts,
        originalState.selectedAlerts,
        stateAfterAlertUpdate.filter,
        stateAfterAlertUpdate.alerts.total,
        processedAlertsInfo
    );

    const allAlertsMap = new Map<string, IAlert>(stateAfterAlertUpdate.alerts.items.map(alert => [alert.id, alert]));

    const validAlerts: IAlert[] = getValidAlerts(stateAfterAlertUpdate);
    const validAlertIDs = new Set<string>(validAlerts.map(alert => alert.id));

    const invalidAlerts: IAlert[] = stateAfterAlertUpdate.alerts.items.filter(alert => !validAlertIDs.has(alert.id));
    const culledInvalidAlerts: IAlert[] = invalidAlerts.slice(0, PAGE_SIZE * INVALID_FILTER_ALERTS_CULL_MODIFIER);

    const culledAlerts: IAlert[] = [...culledInvalidAlerts, ...validAlerts];
    const culledAlertsIDs = new Set<string>(culledAlerts.map(alert => alert.id));

    const culledWindowAlerts: IAlert[] = stateAfterAlertUpdate.window.alerts
        .filter(id => !culledAlertsIDs.has(id))
        .map(id => allAlertsMap.get(id))
        .filter((alert): alert is IAlert => alert != undefined)

    const finalAlerts: IAlert[] = [...culledAlerts, ...culledWindowAlerts];

    const finalState: IAlertState = {
        ...stateAfterAlertUpdate,
        alerts: {
            ...stateAfterAlertUpdate.alerts,
            items: sortAlertsByFilter(stateAfterAlertUpdate.filter.ordering, finalAlerts)
        }
    }

    const window: IAlertWindow = originalState.window.alerts.length === 0 ? getInitialWindow(finalState) : originalState.window;
    return {
        alerts: {
            ...finalState.alerts,
            items: finalAlerts
        },
        selectedAlerts: finalValidatedSelectedAlerts,
        window: window
    };
}

export function handleAlertUpdate(originalState: IAlertState, changes: { id: string } & Partial<IAlert>): Partial<IAlertState> {
    const originalStateWindowAlertsEnsured: IAlertState = _ensureWindowAlerts(originalState);
    const alertInOriginalItems: IAlert | undefined = originalStateWindowAlertsEnsured.alerts.items.find(e => e.id === changes.id);
    if (alertInOriginalItems == null) {
        const filter: IAlertFilter = originalState.filter;

        let totalModifier = 0;
        if (!filter.dismissed && changes.dismissed) {
            totalModifier = -1;
        }
        if (!filter.escalated && changes.escalated) {
            totalModifier = -1;
        }
        if (!filter.muted && changes.muted) {
            totalModifier = -1;
        }

        const newTotal: number = resolveAlertTotal(originalState.alerts.total + totalModifier, originalState.alerts.items, filter);
        return {
            ...originalState,
            alerts: {
                ...originalState.alerts,
                total: newTotal
            }
        }; // Alert isn't in store
    }

    const alertWithIncomingChanges: IAlert = { ...alertInOriginalItems, ...changes };

    const alertsStagedForUpdate: IAlert[] = originalStateWindowAlertsEnsured.alerts.items.filter(alert => alert.id === changes.id);
    const newAlertItems: IAlert[] = alertsStagedForUpdate.map(alert =>
        alert.id === changes.id ? alertWithIncomingChanges : alert
    );

    const stateAfterAlertUpdate: IAlertState = _getStateAfterAlertUpdates(originalStateWindowAlertsEnsured, newAlertItems);

    const finalValidatedSelectedAlerts: ISelectedAlerts = validateSelectedAlert(
        originalState.selectedAlerts,
        stateAfterAlertUpdate.alerts.total,
        changes.id,
        alertMatchesCurrentFilter(alertInOriginalItems, originalState.filter),
        alertMatchesCurrentFilter(alertWithIncomingChanges, stateAfterAlertUpdate.filter)
    );
    const alertsAfterUpdate = stateAfterAlertUpdate.alerts.items;
    const filteredAlertsAfterUpdate = alertsAfterUpdate.filter((alert) => alertMatchesCurrentFilter(alert, originalState.filter))
    const finalState: IAlertState = {
        ...stateAfterAlertUpdate,
        alerts: {
            ...stateAfterAlertUpdate.alerts,
            items: sortAlertsByFilter(stateAfterAlertUpdate.filter.ordering, filteredAlertsAfterUpdate)
        },
        selectedAlerts: finalValidatedSelectedAlerts
    }

    const window: IAlertWindow = originalState.window.alerts.length === 0 ? getInitialWindow(finalState) : originalState.window;
    const uniqueWindows = buildUniqueWindow(window.windows, finalState.alerts.items, originalState.filter, originalState.window.size, originalState.window.interval);

    return {
        alerts: finalState.alerts,
        selectedAlerts: finalState.selectedAlerts,
        window: {
            ...originalState.socketConnected ? originalState.window : window,
            fullAlerts: stateAfterAlertUpdate.window.fullAlerts,
            windows: uniqueWindows
        }
    };
}

// Client Applied Updates
function _validateSelfUpdatedAndWindow(originalState: IAlertState, newAlertItems: IAlert[]): Partial<IAlertState> {
    const processedAlertsInfo: { id: string; alertDataAfterProcessing: IAlert }[] = [];
    if (newAlertItems) {
        newAlertItems.forEach(alert => {
            processedAlertsInfo.push({ id: alert.id, alertDataAfterProcessing: alert });
        })
    }

    const stateAfterAlertUpdateFiltered: IAlertState = _getStateAfterAlertUpdates(originalState, newAlertItems);
    const stateAfterAlertUpdateWindowEnsured: IAlertState = _ensureWindowAlerts(stateAfterAlertUpdateFiltered);
    const finalValidatedUpdatedAlerts: ISelectedAlerts = validateSelectedAlerts(
        originalState.alerts,
        originalState.selectedAlerts,
        stateAfterAlertUpdateFiltered.filter,
        stateAfterAlertUpdateFiltered.alerts.total,
        processedAlertsInfo
    );

    let window = { ...originalState.window };
    const updatedAlertIDs: string[] = newAlertItems.map((alert) => alert.id);
    const updatedFullAlerts = stateAfterAlertUpdateWindowEnsured.window.fullAlerts.filter((alert) => !updatedAlertIDs.includes(alert.id));

    if (updatedFullAlerts.length < 1) {
        window = {
            ...window,
            alerts: [],
            fullAlerts: [],
            currentAlert: 0,
            currentAlertId: undefined,
            selected: []
        }
    } else {
        const alertIDs: string[] = updatedFullAlerts.map((alert) => alert.id);
        const previousAlertId: string | undefined = window.currentAlertId;
        let currentAlertId: string | undefined = alertIDs[0];
        if (previousAlertId != null && alertIDs.includes(previousAlertId)) {
            currentAlertId = previousAlertId;
        }
        window = {
            ...window,
            alerts: window.alerts.filter((alertId) => alertIDs.includes(alertId)),
            fullAlerts: updatedFullAlerts,
            currentAlert: Math.max(alertIDs.indexOf(currentAlertId), 0),
            currentAlertId: currentAlertId,
            selected: [currentAlertId]
        }
    }

    const stateAfterAlertUpdateWithUpdatedWindow: IAlertState = { ...stateAfterAlertUpdateWindowEnsured, window: window };
    return {
        alerts: stateAfterAlertUpdateFiltered.alerts,
        selectedAlerts: finalValidatedUpdatedAlerts,
        window: validateWindow(stateAfterAlertUpdateWithUpdatedWindow)
    };
}

export function handleDismissAlerts(originalState: IAlertState, alertsToDismiss: IAlert[], userID: string): Partial<IAlertState> {
    const alertIDsToDismiss: string[] = alertsToDismiss.map(alert => alert.id);

    const originalStateWindowAlertsEnsured: IAlertState = _ensureWindowAlerts(originalState);

    const newAlertItems: IAlert[] = originalStateWindowAlertsEnsured.alerts.items.map(alert => {
        const dismissAlert: IAlert | undefined = alertsToDismiss.find(ad => ad.id === alert.id);
        if (dismissAlert == null) {
          return alert;
        }
        const dismissedAlertData: IAlert = {
            ...dismissAlert,
            dismissed: true,
            dismissed_by: userID,
            dismiss_timestamp: new Date().toISOString()
        };
        return dismissedAlertData;
    }).filter(alert => alertIDsToDismiss.includes(alert.id));

    return _validateSelfUpdatedAndWindow(originalStateWindowAlertsEnsured, newAlertItems);
}

export function handleEscalateAlerts(originalState: IAlertState, alertsToEscalate: IAlert[], userID: string): Partial<IAlertState> {
    const alertIDsToEscalate: string[] = alertsToEscalate.map(alert => alert.id);

    const originalStateWindowAlertsEnsured: IAlertState = _ensureWindowAlerts(originalState);

    const newAlertItems: IAlert[] = originalStateWindowAlertsEnsured.alerts.items.map(alert => {
        const escalateAlert: IAlert | undefined = alertsToEscalate.find(ae => ae.id === alert.id);
        if (escalateAlert == null) {
          return alert;
        }
        const escalatedAlertData: IAlert = {
            ...escalateAlert,
            escalated: true,
            escalated_by: userID,
            escalation_timestamp: new Date().toISOString()
        };
        return escalatedAlertData;
    }).filter(alert => alertIDsToEscalate.includes(alert.id));

    return _validateSelfUpdatedAndWindow(originalStateWindowAlertsEnsured, newAlertItems);
}

// The optimistic dismiss/escalate removes the alerts from items entirely (they no
// longer match the filter), so a rollback must re-insert the missing ones — mapping
// over the surviving items alone can never bring them back.
export function handleRevertAlerts(originalState: IAlertState, alertsToRestore: IAlert[]): IAlerts {
    const restoredItems: IAlert[] = originalState.alerts.items.map(item => {
        const restoredAlert: IAlert | undefined = alertsToRestore.find(alert => alert.id === item.id);
        return restoredAlert == null ? item : { ...restoredAlert };
    });
    const presentAlertIDs = new Set<string>(restoredItems.map(alert => alert.id));
    const missingAlerts: IAlert[] = alertsToRestore.filter(alert => !presentAlertIDs.has(alert.id));
    const items: IAlert[] = sortAlertsByFilter(originalState.filter.ordering, [...restoredItems, ...missingAlerts]);
    return {
        ...originalState.alerts,
        items: items,
        total: resolveAlertTotal(originalState.alerts.total + missingAlerts.length, items, originalState.filter)
    };
}

// TODO: write unit test
export function addCommentToAlert(alert: IAlert, comment: IAlertComment): IAlert {
    if (alert.id !== comment.alert_id) {
      return alert;
    }

    const existingComments: IAlertComment[] = alert.comments ? [...alert.comments] : [];

    const commentExists: string | boolean | undefined = comment.id && existingComments.some(c => c.id === comment.id);
    if (commentExists) {
      return alert;
    }

    return {
        ...alert,
        comments: [
            ...existingComments,
            {
                ...comment,
                timestamp: DateTime.fromJSDate(new Date(comment.timestamp)).toFormat("yyyy-MM-dd HH:mm:ss")
            }
        ]
    };
}

export function appendActionToAlert(alert: IAlert, alertId: string, action: IAlertAction): IAlert {
    if (alert.id !== alertId) {
      return alert;
    }
    return {
        ...alert,
        alert_actions: [...(alert.alert_actions ?? []), action]
    };
}

export function mergeIdentityDetails(
    alert: IAlert | undefined,
    detectionId: string,
    identityDetails: IIdentityDetail[]
): IAlert | undefined {
    if (alert == null) {
        return undefined;
    }
    const detection = alert.detections[detectionId];
    if (detection == null) {
        return alert;
    }
    return {
        ...alert,
        detections: {
            ...alert.detections,
            [detectionId]: {
                ...detection,
                identity_details: identityDetails,
            },
        },
    };
}

// A background reload's server query can predate a socket-driven appendAlertAction; replacing
// the alert with the response wholesale would silently revert that action out of the audit
// trail until the next load. Union-merge by action id instead, so no action already known
// locally is ever dropped.
export function mergeAlertActionsFromLocal(responseAlert: IAlert, localAlert: IAlert | undefined): IAlert {
    const localActions = localAlert?.alert_actions ?? [];
    if (localActions.length === 0) {
      return responseAlert;
    }
    const responseActions = responseAlert.alert_actions ?? [];
    const responseActionIds = new Set(responseActions.map(a => a.id));
    const missingLocalActions = localActions.filter(a => !responseActionIds.has(a.id));
    if (missingLocalActions.length === 0) {
      return responseAlert;
    }
    return {
        ...responseAlert,
        alert_actions: [...responseActions, ...missingLocalActions]
    };
}

// Local alert data (and thus locally-known alert_actions) can live in state.alerts.items,
// state.window.fullAlerts, or both — see mergeAlertActionsFromLocal's comment for why. Centralised
// here so every load-success reducer merges against the same combined view instead of each
// re-deriving it and risking one being built from only one of the two collections.
export function mergeLocalAlertActions(state: IAlertState, alerts: IAlert[]): IAlert[] {
    const localAlertsById: Map<string, IAlert> = new Map<string, IAlert>();
    for (const alert of [...state.window.fullAlerts, ...state.alerts.items]) {
        const existing = localAlertsById.get(alert.id);
        // Same id in both collections: union their alert_actions rather than letting whichever
        // is seen second silently overwrite the other and drop its disjoint actions.
        localAlertsById.set(alert.id, existing == null ? alert : mergeAlertActionsFromLocal(existing, alert));
    }
    return alerts.map(alert => mergeAlertActionsFromLocal(alert, localAlertsById.get(alert.id)));
}

export function mapSocketLocksToRecord(locks: IAlertLock[]): Record<string, IAlertLock> {
    return locks.reduce((map, obj) => {
        map[obj.alertID] = obj;
        return map;
    }, {} as Record<string, IAlertLock>)
}

// TODO: write unit test
function _updateWindow(alertIds: string[], window: IAlertWindow, currentAlerts: IAlert[], filter?: IAlertFilter): IAlertWindow {
    const finalWindow = { ...window };

    if (!alertIds || !window) {
        return finalWindow;
    }

    if (alertIds.length < 1 || window.windows.length < 1) {
        return finalWindow;
    }

    const alertWindowIndex = findAlertGroupIndex(window.windows, alertIds[alertIds.length - 1]);
    const alertWindow = window.windows[alertWindowIndex > -1 ? alertWindowIndex : 0];
    const filteredOutAlerts = alertWindow.filter((_alertId) => alertIds.includes(_alertId));
    const currentAlertsMap = new Map<string, IAlert>(currentAlerts.map((alert) => [alert.id, alert]));
    const validFilteredOutAlerts = filteredOutAlerts.filter((alertId) => {
        const alert = currentAlertsMap.get(alertId);
        if (!alert) {
            return false;
        }
        if (filter && !alertMatchesCurrentFilter(alert, filter)) {
            return false;
        }
        return true;
    });
    const firstAlert = validFilteredOutAlerts[0];

    if (validFilteredOutAlerts.length < 1) {
        return finalWindow;
    }

    // Keep the operator on the alert they were viewing if it is still in the window —
    // lock/state refreshes must not snap the view back to the first alert.
    const previousAlertId: string | undefined = window.currentAlertId;
    let currentAlertId: string | undefined = firstAlert;
    if (previousAlertId != null && validFilteredOutAlerts.includes(previousAlertId)) {
        currentAlertId = previousAlertId;
    }

    return {
        ...finalWindow,
        alerts: validFilteredOutAlerts,
        fullAlerts: currentAlerts.filter((alert) => validFilteredOutAlerts.includes(alert.id)),
        currentAlertId: currentAlertId,
        currentAlert: Math.max(validFilteredOutAlerts.indexOf(currentAlertId), 0),
        currentWindowIndex: alertWindowIndex,
        selected: [currentAlertId]
    }
}

export function updateWindowWithLocks(window: IAlertWindow, locks: IAlertLock[], currentAlerts: IAlert[], userId: string, filter?: IAlertFilter): IAlertWindow {
    const alertIds = locks.filter((lock) => lock.userID == userId).map((alertLock) => alertLock.alertID);
    return _updateWindow(alertIds, window, currentAlerts, filter);
}

export function updateWindowWithoutLocks(window: IAlertWindow, currentAlerts: IAlert[], filter?: IAlertFilter): IAlertWindow {
    const alertIds: string[] = window.windows[0];
    return _updateWindow(alertIds, window, currentAlerts, filter);
}

// TODO: This function is unused — remove or replace during alert state cleanup
export function oldLoadAlertsSuccessResetState(state: IAlertState, alerts: IAlert[], total: number, resetState: boolean): [IAlertState, IAlert[]] {
    let currentState = state;
    let alertsToAdd: IAlert[] = [];

    if (resetState) {
        const oldStateAlertsMap = new Map<string, IAlert>(state.alerts.items.map(alert => [alert.id, alert]));
        alertsToAdd = alerts.map(incomingAlert => oldStateAlertsMap.get(incomingAlert.id) || incomingAlert);
        currentState = {
            ...state,
            alerts: {
                ...state.alerts,
                items: [],
            },
            window: {
                ...state.window,
                ...DEFAULT_EMPTY_WINDOW
            }
        };
    } else {
        if (total <= PAGE_SIZE) {
            const validFilterAlerts: IAlert[] = getValidAlerts(currentState);
            const validFilterAlertIDs = new Set<string>(validFilterAlerts.map(alert => alert.id));
            const windowAlertIDs: string[] = currentState.window.alerts;
            const invalidFilterAlerts: IAlert[] = currentState.alerts.items.filter(alert => !validFilterAlertIDs.has(alert.id) || windowAlertIDs.includes(alert.id));
            currentState = {
                ...currentState,
                alerts: {
                    ...currentState.alerts,
                    items: invalidFilterAlerts
                }
            }
        }
        const storeAlertIDs = new Set<string>(currentState.alerts.items.map(e => e.id));
        alertsToAdd = alerts.filter(alert => !storeAlertIDs.has(alert.id));
    }

    return [currentState, alertsToAdd];
}


// TODO: This function is unused — remove or replace during alert state cleanup
export function exampleFunction(numbers: number[]): number {
    return numbers.reduce((acc, curr) => acc + curr, 0);
}
